/** Foreground strip from `top` to the bottom edge, with a texture per material. */
import type { GroundLayer } from '@dropstack/core';
import type { SceneSize } from '../layout.ts';
import { range, rngFor, valueNoise } from '../noise.ts';
import { circlePath, ellipsePath, pick, rgba, rimOf, roundRectPath, shade, smoothLine, strokeRim, twinkle, vGradient, type Ctx, type Point } from './draw.ts';
import type { Painter } from './types.ts';

/** Top edge of the strip: gently undulating (snow and candy are bumpier). */
function groundEdge(layer: GroundLayer, size: SceneSize, seed: number): Point[] {
  const top = layer.top * size.height;
  const bumpy = layer.texture === 'snow' || layer.texture === 'candy' ? 2.2 : 1;
  const pts: Point[] = [];
  const step = Math.max(4, size.unit * 0.02);
  for (let x = -size.bleed - 4; x <= size.width + size.bleed + 4 + step; x += step) {
    const u = x / size.unit;
    pts.push({ x, y: top + size.unit * 0.008 * bumpy * valueNoise(seed, u * 5) - size.unit * 0.004 * valueNoise(seed + 1, u * 17) });
  }
  return pts;
}

function tuft(ctx: Ctx, x: number, y: number, h: number, color: string): void {
  ctx.beginPath();
  ctx.moveTo(x - h * 0.35, y);
  ctx.quadraticCurveTo(x - h * 0.3, y - h * 0.5, x - h * 0.55, y - h * 0.95);
  ctx.quadraticCurveTo(x - h * 0.1, y - h * 0.55, x, y - h * 0.3);
  ctx.quadraticCurveTo(x + h * 0.05, y - h * 0.8, x + h * 0.1, y - h * 1.15);
  ctx.quadraticCurveTo(x + h * 0.25, y - h * 0.6, x + h * 0.2, y - h * 0.3);
  ctx.quadraticCurveTo(x + h * 0.45, y - h * 0.6, x + h * 0.6, y - h * 0.85);
  ctx.quadraticCurveTo(x + h * 0.5, y - h * 0.35, x + h * 0.4, y);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

function flower(ctx: Ctx, x: number, y: number, r: number, petal: string, size: SceneSize): void {
  ctx.beginPath();
  for (let k = 0; k < 5; k++) {
    const a = (k / 5) * Math.PI * 2 - Math.PI / 2;
    circlePath(ctx, x + Math.cos(a) * r * 0.62, y + Math.sin(a) * r * 0.62, r * 0.48);
  }
  ctx.fillStyle = petal;
  ctx.fill();
  strokeRim(ctx, rgba(rimOf(petal), 0.6), size, 0.5);
  ctx.beginPath();
  circlePath(ctx, x, y, r * 0.36);
  ctx.fillStyle = petal === '#ffe066' || petal === '#fff3a3' ? '#ff9f43' : '#ffd43b';
  ctx.fill();
}

type TexturePainter = (ctx: Ctx, layer: GroundLayer, size: SceneSize, seed: number, edge: readonly Point[]) => void;

/** Scatter `n` points over the strip (never above its top edge). */
function scatter(layer: GroundLayer, size: SceneSize, seed: number, n: number, salt: number): Point[] {
  const rng = rngFor(seed, salt);
  const top = layer.top * size.height + size.unit * 0.02;
  return Array.from({ length: n }, () => ({ x: range(rng, -size.bleed, size.width + size.bleed), y: range(rng, top, size.height) }));
}

function density(size: SceneSize, perScreen: number): number {
  return Math.round((perScreen * size.width) / Math.max(1, size.unit));
}

const textures: Record<GroundLayer['texture'], TexturePainter> = {
  grass(ctx, layer, size, seed, edge) {
    const accent = layer.accent ?? shade(layer.color, -0.15);
    edge.forEach((p, i) => {
      if (i % 2 === 0) tuft(ctx, p.x, p.y + size.unit * 0.004, size.unit * (0.02 + 0.012 * ((i * 7) % 3)), layer.color);
    });
    for (const p of scatter(layer, size, seed, density(size, 26), 3)) tuft(ctx, p.x, p.y, size.unit * 0.016, accent);
  },
  meadow(ctx, layer, size, seed, edge) {
    textures.grass(ctx, layer, size, seed, edge);
    const colors = layer.flowers?.length ? layer.flowers : ['#ffffff', '#ffd43b'];
    scatter(layer, size, seed, density(size, 22), 5).forEach((p, i) => flower(ctx, p.x, p.y, size.unit * 0.009, pick(colors, i), size));
  },
  sand(ctx, layer, size, seed) {
    const accent = layer.accent ?? shade(layer.color, -0.12);
    ctx.strokeStyle = rgba(accent, 0.8);
    ctx.lineWidth = Math.max(1, size.unit * 0.004);
    ctx.lineCap = 'round';
    for (const p of scatter(layer, size, seed, density(size, 10), 7)) {
      const w = size.unit * 0.05;
      ctx.beginPath();
      ctx.moveTo(p.x - w / 2, p.y);
      ctx.quadraticCurveTo(p.x, p.y - size.unit * 0.008, p.x + w / 2, p.y);
      ctx.stroke();
    }
    ctx.fillStyle = accent;
    ctx.beginPath();
    for (const p of scatter(layer, size, seed, density(size, 60), 9)) circlePath(ctx, p.x, p.y, size.unit * 0.0025);
    ctx.fill();
    // A few pink-and-cream shells.
    scatter(layer, size, seed, density(size, 3), 11).forEach((p, i) => {
      const r = size.unit * 0.009;
      const color = i % 2 ? '#ffc9c9' : '#fff4e6';
      ctx.beginPath();
      ctx.moveTo(p.x - r, p.y);
      ctx.quadraticCurveTo(p.x, p.y - r * 2, p.x + r, p.y);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
      strokeRim(ctx, rimOf(color), size, 0.5);
    });
  },
  snow(ctx, layer, size, seed) {
    const accent = layer.accent ?? shade(layer.color, -0.08);
    ctx.fillStyle = rgba(accent, 0.8);
    ctx.beginPath();
    for (const p of scatter(layer, size, seed, density(size, 9), 13)) ellipsePath(ctx, p.x, p.y, size.unit * 0.05, size.unit * 0.008);
    ctx.fill();
    scatter(layer, size, seed, density(size, 14), 15).forEach((p, i) => twinkle(ctx, p.x, p.y, size.unit * (0.006 + (i % 3) * 0.002), '#ffffff', 0.95));
  },
  rock(ctx, layer, size, seed) {
    const accent = layer.accent ?? shade(layer.color, -0.15);
    const rng = rngFor(seed, 17);
    for (const p of scatter(layer, size, seed, density(size, 16), 19)) {
      const r = size.unit * range(rng, 0.008, 0.02);
      ctx.beginPath();
      ellipsePath(ctx, p.x, p.y, r * 1.4, r);
      ctx.fillStyle = accent;
      ctx.fill();
      strokeRim(ctx, rimOf(accent), size, 0.6);
      ctx.beginPath();
      ellipsePath(ctx, p.x - r * 0.4, p.y - r * 0.4, r * 0.5, r * 0.25);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.fill();
    }
  },
  moss(ctx, layer, size, seed, edge) {
    const accent = layer.accent ?? shade(layer.color, -0.12);
    ctx.beginPath();
    for (const p of scatter(layer, size, seed, density(size, 10), 21)) ellipsePath(ctx, p.x, p.y, size.unit * 0.04, size.unit * 0.012);
    ctx.fillStyle = rgba(accent, 0.7);
    ctx.fill();
    ctx.beginPath();
    for (const p of scatter(layer, size, seed, density(size, 12), 23)) ellipsePath(ctx, p.x, p.y, size.unit * 0.025, size.unit * 0.008);
    ctx.fillStyle = rgba(shade(layer.color, 0.2), 0.7);
    ctx.fill();
    edge.forEach((p, i) => {
      if (i % 3 === 0) tuft(ctx, p.x, p.y + size.unit * 0.004, size.unit * 0.018, layer.color);
    });
  },
  candy(ctx, layer, size, seed, edge) {
    // Icing band along the top with rounded drips.
    const icing = layer.accent ?? '#ffffff';
    const band = size.unit * 0.012;
    const lower = [...edge].reverse().map((p, i) => ({ x: p.x, y: p.y + band + (i % 4 === 0 ? size.unit * 0.014 : 0) }));
    ctx.beginPath();
    smoothLine(ctx, edge);
    smoothLine(ctx, lower, true);
    ctx.closePath();
    ctx.fillStyle = icing;
    ctx.fill();
    strokeRim(ctx, rgba(shade(icing, -0.25), 0.6), size, 0.6);
    // Sprinkles.
    const colors = layer.flowers?.length ? layer.flowers : ['#ff6b6b', '#ffd43b', '#4dabf7'];
    scatter(layer, size, seed, density(size, 50), 25).forEach((p, i) => {
      const len = size.unit * 0.012;
      ctx.save();
      ctx.translate(p.x, p.y + band);
      ctx.rotate((i * 2.39996) % Math.PI);
      ctx.beginPath();
      roundRectPath(ctx, -len / 2, -len * 0.18, len, len * 0.36, len * 0.18);
      ctx.fillStyle = pick(colors, i);
      ctx.fill();
      ctx.restore();
    });
  },
};

export const groundPainter: Painter<GroundLayer> = {
  extent: (layer, { size }) => ({ top: layer.top * size.height - size.unit * 0.06, bottom: size.height }),
  still(ctx, layer, { size, seed }) {
    const edge = groundEdge(layer, size, seed);
    const top = layer.top * size.height;
    const first = edge[0];
    const last = edge[edge.length - 1];
    if (!first || !last) return;
    ctx.beginPath();
    smoothLine(ctx, edge);
    ctx.lineTo(last.x, size.height + 2);
    ctx.lineTo(first.x, size.height + 2);
    ctx.closePath();
    ctx.fillStyle = vGradient(ctx, top, size.height, [shade(layer.color, 0.08), layer.color, shade(layer.color, -0.1)]);
    ctx.fill();
    ctx.save();
    ctx.clip();
    textures[layer.texture](ctx, layer, size, seed, edge);
    ctx.restore();
    ctx.beginPath();
    smoothLine(ctx, edge);
    strokeRim(ctx, layer.rim ?? rimOf(layer.color), size);
  },
};
